import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import MediaStorageService from "../services/media_storage_service"
import { I18n } from 'i18n-js';
import locales from '../locales.json';

export default class extends Controller {
  static targets = [
    "modal",
    "preview",
    "canvas",
    "previewContainer",
    "photoButton",
    "recordButton",
    "alertContainer"
  ]

  static values = {
    buildingComplexId: String,
    roadId: String
  }

  async connect() {
    this.i18n = new I18n(locales);
    this.i18n.locale = document.querySelector("meta[name='locale']").content

    this.recordedChunks = []
    this.mediaStorage = new MediaStorageService()
    await this.mediaStorage.initDB();
  }

  async initializeMedia() {
    try {
      // First, get list of available cameras
      const devices = await navigator.mediaDevices.enumerateDevices()
      const videoDevices = devices.filter(device => device.kind === 'videoinput')
      
      // Try to find the back camera
      const backCamera = videoDevices.find(device => 
        device.label.toLowerCase().includes('back') || 
        device.label.toLowerCase().includes('rear')
      )
  
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          // If we found a back camera, use its deviceId, otherwise fall back to 'environment'
          ...(backCamera 
            ? { deviceId: { exact: backCamera.deviceId } }
            : { facingMode: { exact: "environment" } }
          ),
          // Optionally, you can add more constraints
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: true
      })
      
      this.previewTarget.srcObject = stream
      this.mediaStream = stream
    } catch (error) {
      console.error('Error accessing media devices:', error)
      this.showError(this.i18n.t("media_capture.device.access_error"))
    }
  }
  

  async takePhoto() {
    if (!this.mediaStream) return

    const canvas = this.canvasTarget
    const video = this.previewTarget
    
    // Set canvas dimensions to match video
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    
    // Draw video frame to canvas
    const context = canvas.getContext('2d')
    context.drawImage(video, 0, 0, canvas.width, canvas.height)
    
    // Convert to blob
    try {
      const stream = this.previewTarget.srcObject;
      if (!stream) {
        this.showError(this.i18n.t("media_capture.device.camera_error"));
        return;
      }

      const blob = await new Promise(resolve => {
        canvas.toBlob(resolve, 'image/jpeg', 0.95)
      })
      
      // Save to IndexedDB
      await this.mediaStorage.saveMedia(this.roadIdValue, blob, 'image')
      this.showSuccess(this.i18n.t("media_capture.photo.success"))
    } catch (error) {
      console.error('Error capturing photo:', error)
      this.showError(this.i18n.t("media_capture.photo.error"))
    }
  }

  startRecording() {
    if (!this.mediaStream) return
    
    try {
      const options = this.getMediaRecorderOptions()
      this.mediaRecorder = new MediaRecorder(this.mediaStream, options)
      this.recordedChunks = []
      
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.recordedChunks.push(event.data)
        }
      }
      
      this.mediaRecorder.onstop = async () => {
        //const blob = new Blob(this.recordedChunks, { type: 'video/webm' })
        const blob = new Blob(this.recordedChunks, { 
          type: this.mediaRecorder.mimeType || 'video/webm' 
        })

        // If needed, convert to MP4 format
        //const mp4Blob = await this.ensureMp4Format(blob)
        
        //await this.mediaStorage.saveMedia(this.roadIdValue, blob, 'video')
        await this.mediaStorage.saveMedia(this.roadIdValue, blob, 'video')
        this.showSuccess(this.i18n.t("media_capture.video.success"))
      }
      
      // Start recording
      this.mediaRecorder.start()
      
      // Update UI
      const recordButton = this.recordButtonTarget
      recordButton.innerHTML = '<i class="bi bi-stop-fill fs-4"></i>'
      recordButton.classList.add('btn-warning')
      recordButton.classList.remove('btn-danger')
      recordButton.dataset.action = 'click->media-capture#stopRecording'
    } catch (error) {
      console.error('Error starting recording:', error)
      this.showError(this.i18n.t("media_capture.video.error_start"))
    }
  }
  
  stopRecording() {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      try {
        this.mediaRecorder.stop()
        
        // Reset UI
        const recordButton = this.recordButtonTarget
        recordButton.innerHTML = '<i class="bi bi-record-circle fs-4"></i>'
        recordButton.classList.add('btn-danger')
        recordButton.classList.remove('btn-warning')
        recordButton.dataset.action = 'click->media-capture#startRecording'
      } catch (error) {
        console.error('Error stopping recording:', error)
        this.showError(this.i18n.t("media_capture.video.error_stop"))
      }
    }
  }
  

  closeModal() {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop())
      this.mediaStream = null
    }
    
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.stopRecording()
    }
    
    this.previewTarget.srcObject = null
    this.previewContainerTarget.innerHTML = ''
  }

  showError(message) {
    this.showAlert('danger', message);
  }

  showSuccess(message) {
    this.showAlert('success', message);
  }

  disconnect() {
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop())
    }
  }

  showAlert(type, message) {
    const alertHtml = `
      <div class="alert alert-${type} alert-dismissible fade show" role="alert"
           style="background-color: rgba(0,0,0,0.7); border: none; color: white;">
        ${message}
        <button type="button" class="btn-close btn-close-white" data-bs-dismiss="alert" aria-label="Close"></button>
      </div>
    `;
    
    this.alertContainerTarget.innerHTML = alertHtml;

    // Auto-dismiss after 3 seconds
    setTimeout(() => {
      const alert = this.alertContainerTarget.querySelector('.alert');
      if (alert) {
        alert.classList.remove('show');
        setTimeout(() => {
          this.alertContainerTarget.innerHTML = '';
        }, 150);
      }
    }, 3000);
  }

  getMediaRecorderOptions() {
    const options = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm;codecs=h264,opus',
      'video/webm'
    ]
  
    for (const mimeType of options) {
      if (MediaRecorder.isTypeSupported(mimeType)) {
        console.log(`Using MIME type: ${mimeType}`)
        return { mimeType }
      }
    }
  
    console.log('No specified MIME type supported, using browser default')
    return {} // Let the browser choose the format
  }

}
